'use client';

import cn from 'classnames';
import { Text } from '@/ui-components/atoms/Text/Text';
import { Button } from '@/ui-components/atoms/Button/Button';
import { ChannelBadge } from '../ChannelCardStrip/ChannelBadge';
import {
    ScrollableChannelTiles,
    type ScrollableChannelTilesHandle,
} from '@/ui-components/molecules/ScrollableChannelTiles/ScrollableChannelTiles';
import { PopularChannelsModal } from '../PopularChannelsModal/PopularChannelsModal';
import { IconDropDown } from '@/assets/icons/IconDropdown';
import IconCheckmarkSm from '@/assets/icons/icon-checkmark-sm.svg';
import { isColorBright } from '@/packages/color/colorBrightness';
import { CSSPropsVars } from '@/interfaces/types/CSSPropsVars';
import styles from './singleplancard.module.scss';
import type { SinglePlanCardProps, SinglePlanCardTheme } from './SinglePlanCard.types';
import { useEffect, useRef, useState } from 'react';
export type { SinglePlanCardProps } from './SinglePlanCard.types';
const THEME_CLASS: Record<SinglePlanCardTheme, string> = {
    'SXM Blue': styles.themeSxmBlue,
    Pink: styles.themePink,
};
const CHECK_WIDTH = 17;
const CHECK_HEIGHT = 12;
const TILES_NUDGE_THRESHOLD = 0.5;

export function SinglePlanCard({
    index = 0,
    // theming
    theme = 'SXM Blue',
    borderColor,
    // banner / badge
    flagText,
    flagColor = '#ffffff',
    hideBanner = false,
    badgeText,
    hideBadge = false,
    // body
    eyebrow,
    title,
    emphasizeTitle = false,
    subhead,
    offerDetails,
    longDescription,
    // popular channels
    channels,
    isScrollableChannelTiles,
    caption,
    // listening options
    listeningOptions,
    hideListeningOptions = false,
    // features
    features,
    featureListStyle = 'none',
    // accordion
    accordionStateMobile = 'closed',
    accordionStateDesktop = 'closed',
    accordionShowText = 'Explore plan details',
    accordionHideText = 'Hide plan details',
    accordionMobilePointer = 'Hide All',
    accordionDesktopPointer = 'Hide All',
    // cta
    cta,
    ctaLegal,
    ctaLegalPosition = 'above',
    ctaPlacement = 'trailing',
    hideCta = false,
    isSingleCard = false,
    // layout
    className,
}: SinglePlanCardProps): React.JSX.Element {
    const cardVars: CSSPropsVars = {
        ...(borderColor && { '--card-border-color': borderColor }),
    };
    const flagVars: CSSPropsVars = { '--flag-background-color': flagColor };
    const showListeningOptions = !hideListeningOptions && !!listeningOptions?.length;
    const hasMobileAccordion = accordionStateMobile !== 'none';
    const hasDesktopAccordion = accordionStateDesktop !== 'none';
    const [isMobileAccordionOpen, setIsMobileAccordionOpen] = useState(accordionStateMobile === 'open');
    const [isDesktopAccordionOpen, setIsDesktopAccordionOpen] = useState(accordionStateDesktop === 'open');
    function buildAccordionLinkName(isOpen: boolean) {
        const label = isOpen ? accordionHideText : accordionShowText;
        return `Toggle:${typeof label === 'string' ? label : 'Plan details'}`;
    }
    // Both variants are always computed and rendered together; CSS media queries (not JS)
    // decide which one is visible, so there's no post-hydration flicker/pop-in from a
    // viewport check resolving late.
    const mobileAccordion = {
        pointer: accordionMobilePointer,
        hasAccordion: hasMobileAccordion,
        initialOpen: accordionStateMobile === 'open',
        isOpen: isMobileAccordionOpen,
        setOpen: setIsMobileAccordionOpen,
    };
    const desktopAccordion = {
        pointer: accordionDesktopPointer,
        hasAccordion: hasDesktopAccordion,
        initialOpen: accordionStateDesktop === 'open',
        isOpen: isDesktopAccordionOpen,
        setOpen: setIsDesktopAccordionOpen,
    };
    const mobileLinkName = buildAccordionLinkName(mobileAccordion.isOpen);
    const desktopLinkName = buildAccordionLinkName(desktopAccordion.isOpen);
    const hasChannelTiles = !!channels?.popularChannels?.length;
    const [popularChannelModalOpen, setPopularChannelModalOpen] = useState(false);
    const channelTilesWrapperRef = useRef<HTMLDivElement>(null);
    const channelTilesRef = useRef<ScrollableChannelTilesHandle>(null);
    const hasNudgedTilesRef = useRef(false);
    const handleViewMoreClick = () => {
        setPopularChannelModalOpen(true);
    };
    const handleClosePopularChannelModal = () => {
        setPopularChannelModalOpen(false);
    };
    // Nudge one tile the first time the carousel scrolls into view, hinting that it scrolls.
    useEffect(() => {
        const wrapper = channelTilesWrapperRef.current;
        if (!wrapper || typeof IntersectionObserver === 'undefined') {
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting && !hasNudgedTilesRef.current) {
                    channelTilesRef.current?.animateOneTile?.();
                    hasNudgedTilesRef.current = true;
                }
            },
            { threshold: TILES_NUDGE_THRESHOLD },
        );
        observer.observe(wrapper);
        return () => observer.disconnect();
    }, [hasChannelTiles]);

    const channelsCaption = (caption || channels?.channelsLinkLabel) && (
        <div className={styles.seemoreWrapper}>
            <span>{caption}</span>
            <button onClick={handleViewMoreClick} className={styles.seeMore}>
                {channels?.channelsLinkLabel}
            </button>
        </div>
    );

    const channelTilesBlock = hasChannelTiles && (
        <div className={styles.channelTiles} ref={index === 0 ? channelTilesWrapperRef : null}>
            <ScrollableChannelTiles
                items={channels?.popularChannels ?? []}
                arrowVariant="overlay"
                ref={channelTilesRef}
                isScrollableChannelTiles={isScrollableChannelTiles}
            />
        </div>
    );

    const listeningOptionsBlock = showListeningOptions && (
        <div className={styles.listeningOptions}>
            <ul className={styles.listeningOptionsList}>
                {listeningOptions?.slice(0, 3).map((option, index) => (
                    <li key={index} className={styles.listeningOption}>
                        {/*
                         * Fixed-size box: reserves the icon's layout up front so a
                         * late-loading device image can't shift the card (CLS). The
                         * consumer's icon is contained inside it, so its own aspect
                         * ratio is preserved.
                         * alt empty/undefined => decorative; consumer sets alt on the element.
                         */}
                        <div className={styles.listeningOptionIcon}>{option.icon}</div>
                        {option.alt ? <span className="show-for-sr">{option.alt}</span> : null}
                        {(option.title || option.additionalText) && (
                            <div className={styles.listeningOptionText}>
                                {option.title && (
                                    <Text tag="div" variant="text-bold" className={styles.listeningOptionTitle}>
                                        {option.title}
                                    </Text>
                                )}
                                {option.additionalText && (
                                    <Text tag="div" variant="text-detail" className={styles.listeningOptionAdditional}>
                                        {option.additionalText}
                                    </Text>
                                )}
                            </div>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );

    const longDescriptionBlock = longDescription && (
        <Text tag="div" variant="text-large" className={styles.longDescription}>
            {longDescription}
        </Text>
    );

    const featuresBlock = !!features?.length && (
        <ul
            className={cn(styles.features, {
                [styles[`features--${featureListStyle}`]]: featureListStyle !== 'none',
            })}
        >
            {features.map((feature, index) => (
                <li key={index} className={styles.feature}>
                    {featureListStyle === 'checkmark' && (
                        <IconCheckmarkSm
                            className={styles.featureCheck}
                            width={CHECK_WIDTH}
                            height={CHECK_HEIGHT}
                            aria-hidden="true"
                        />
                    )}
                    <span className={styles.featureLabel}>{feature}</span>
                </li>
            ))}
        </ul>
    );
    // "Accordion Pointer" = 'Description' pulls longDescription out of the accordion (always visible),
    // leaving only features collapsible. Otherwise ('Value Props' / unset) both stay inside together.
    // Computed once per viewport variant, since mobile/desktop pointers can differ.
    function buildPointerContent(pointer: SinglePlanCardProps['accordionMobilePointer']) {
        const isPartialPointer = pointer === 'Partial Value Props';
        const isDescriptionPointer = pointer === 'Description';
        const isChannelsPointer = pointer === 'Channels';
        const isHideAllPointer = pointer === 'Hide All';
        const showChannelsWhenClosed = !isHideAllPointer;
        const pullsDescriptionOutWhenClosed = isPartialPointer || isDescriptionPointer;
        const leadContent = pullsDescriptionOutWhenClosed ? longDescriptionBlock : null;
        const details = pullsDescriptionOutWhenClosed ? (
            featuresBlock
        ) : (
            <>
                {longDescriptionBlock}
                {featuresBlock}
            </>
        );
        const hasDetailsContent = !!longDescription || !!features?.length;
        return {
            leadContent,
            details,
            hasDetailsContent,
            isPartialPointer,
            isChannelsPointer,
            isHideAllPointer,
            isDescriptionPointer,
            showChannelsWhenClosed,
        };
    }
    const mobilePointerContent = buildPointerContent(mobileAccordion.pointer);
    const desktopPointerContent = buildPointerContent(desktopAccordion.pointer);
    // Single CTA block (legal + button). `ctaPlacement` drives whether it sits
    // above (leading) or below (trailing) the content.
    const legal = ctaLegal && (
        <Text tag="p" className={cn(styles.ctaLegal, { [styles.textAboveCta]: ctaLegalPosition === 'above' })}>
            {ctaLegal}
        </Text>
    );
    const ctaBlock = !hideCta && (cta || ctaLegal) && (
        <div className={cn(styles.ctaContainer, { [styles.ctaContainerLeading]: ctaPlacement === 'leading' })}>
            {ctaLegalPosition === 'above' && legal}
            {cta && (
                <Button {...cta} type="button" fitting="fill-container" className={cn(styles.cta, cta.className)} />
            )}
            {ctaLegalPosition === 'below' && legal}
        </div>
    );
    // Renders one viewport variant's accordion/details section. `variantClassName` (mobileOnly /
    // desktopOnly) is a CSS media-query class that controls visibility — never JS — so there is
    // no hydration-timing gap where the wrong variant briefly shows.
    function renderAccordionVariant(
        accordion: typeof mobileAccordion,
        content: ReturnType<typeof buildPointerContent>,
        linkName: string,
        variantClassName: string,
    ) {
        if (!content.hasDetailsContent) return null;
        const usesPartialSpacing = content.isPartialPointer || content.isDescriptionPointer;
        return (
            <div className={variantClassName}>
                {content.leadContent && <div className={styles.leadDetails}>{content.leadContent}</div>}
                {accordion.hasAccordion ? (
                    <>
                        {content.isPartialPointer && !accordion.isOpen && (
                            <div className={styles.staticDetailsTruncated} aria-hidden="true">
                                {content.details}
                            </div>
                        )}
                        <details
                            className={cn(styles.accordion, { [styles.fadedValueProps]: !!content.isPartialPointer })}
                            open={accordion.initialOpen}
                            onToggle={(e) => accordion.setOpen(e.currentTarget.open)}
                        >
                            <summary
                                className={styles.accordionToggle}
                                data-linkname={linkName}
                                data-cy="single-plan-card-accordion-toggle"
                            >
                                <span className={styles.accordionShowLabel}>{accordionShowText}</span>
                                <span className={styles.accordionHideLabel}>{accordionHideText}</span>
                                <span className={styles.accordionCaret} aria-hidden="true">
                                    <IconDropDown />
                                </span>
                            </summary>
                            <div className={styles.accordionContent} data-cy="single-plan-card-accordion-content">
                                {content.details}
                            </div>
                        </details>
                    </>
                ) : (
                    <div
                        className={cn(styles.staticDetails, {
                            [styles.partialStaticDetails]: usesPartialSpacing,
                            [styles.staticDetailsWithTopSpacing]: !usesPartialSpacing,
                        })}
                    >
                        {content.details}
                    </div>
                )}
            </div>
        );
    }
    return (
        <div className={cn(styles.wrapper, className, { [styles.singleCard]: !!isSingleCard })}>
            <div
                className={cn(styles.card, THEME_CLASS[theme], { [styles.hasBorder]: !!borderColor })}
                style={cardVars}
                data-cy="single-plan-card"
            >
                {!hideBanner && (
                    <div
                        className={cn(styles.flag, { [styles.whiteText]: !isColorBright(flagColor) })}
                        style={flagVars}
                    >
                        {flagText}
                    </div>
                )}
                <div className={styles.body}>
                    {badgeText && !hideBadge && <ChannelBadge badgeText={badgeText} className={styles.badge} />}
                    {eyebrow && (
                        <Text tag="div" variant="text-detail" className={styles.eyebrow}>
                            {eyebrow}
                        </Text>
                    )}
                    {title && <div className={cn(styles.title, { [styles.titleAccent]: emphasizeTitle })}>{title}</div>}
                    {offerDetails && (
                        <Text tag="div" variant="text-detail" className={styles.offerDetails}>
                            {offerDetails}
                        </Text>
                    )}
                    {subhead && (
                        <Text tag="div" variant="text-medium" className={styles.subhead}>
                            {subhead}
                        </Text>
                    )}
                    {ctaPlacement === 'leading' && ctaBlock}
                    {listeningOptionsBlock}
                    {(mobilePointerContent.showChannelsWhenClosed ||
                        !mobileAccordion.hasAccordion ||
                        mobileAccordion.isOpen) && (
                        <div className={styles.mobileOnly}>
                            {channelsCaption}
                            {channelTilesBlock}
                        </div>
                    )}
                    {(desktopPointerContent.showChannelsWhenClosed ||
                        !desktopAccordion.hasAccordion ||
                        desktopAccordion.isOpen) && (
                        <div className={styles.desktopOnly}>
                            {channelsCaption}
                            {channelTilesBlock}
                        </div>
                    )}
                    {renderAccordionVariant(mobileAccordion, mobilePointerContent, mobileLinkName, styles.mobileOnly)}
                    {renderAccordionVariant(
                        desktopAccordion,
                        desktopPointerContent,
                        desktopLinkName,
                        styles.desktopOnly,
                    )}
                    {ctaPlacement !== 'leading' && ctaBlock}
                </div>
            </div>
            {popularChannelModalOpen && (
                <PopularChannelsModal
                    open={popularChannelModalOpen}
                    onClose={handleClosePopularChannelModal}
                    tabs={channels?.modalTabs || []}
                    heading={channels?.modalHeading}
                    dataLocalpagename={channels?.modalDataLocalpagename}
                />
            )}
        </div>
    );
}
